import Auth from '../api/auth';
import {
  clearCookiesOnLogout,
  deleteIndexedDBOnLogout,
  extractAuthHeaders,
  hasRequiredAuthHeaders,
  storeAuthHeaders,
} from '../store/utils/api';
import { noteServerBuild } from 'dashboard/helper/appVersion';
import {
  getHeldAuth,
  headersChanged,
  mayWriteAuthCookie,
  setHeldAuth,
} from 'dashboard/helper/authSync';

let forcedLogoutInProgress = false;

const isDeviseAuthenticationFailure = error => {
  if (error?.response?.status !== 401) return false;

  const data = error?.response?.data || {};
  if (data.logout === true) return true;

  const requestUrl = String(error?.config?.url || '');
  if (requestUrl.includes('/auth/validate_token')) return true;

  // Devise returns this shape when the browser still has a local auth cookie
  // but its token has expired or been invalidated. Permission failures in this
  // app use `error` (singular), so they must not log the user out.
  return (
    Array.isArray(data.errors) &&
    data.errors.some(message =>
      /sign in|sign up before continuing|authenticate/i.test(String(message))
    )
  );
};

const shouldForceLoginRedirect = error => isDeviseAuthenticationFailure(error);

const forceLogoutToLoginPage = () => {
  if (forcedLogoutInProgress) return;
  forcedLogoutInProgress = true;

  Promise.resolve(deleteIndexedDBOnLogout())
    .catch(() => {
      // Best effort cleanup; continue redirect flow even if IndexedDB delete fails.
    })
    .finally(() => {
      clearCookiesOnLogout('/app/login', { ownUid: getHeldAuth()?.uid });
    });
};

const parseErrorCode = error => {
  if (shouldForceLoginRedirect(error)) {
    forceLogoutToLoginPage();
  }
  return Promise.reject(error);
};

// axios waits forever unless a timeout is set. One response lost on a flaky
// network (seen behind VPNs) left fetchAllConversations' promise pending, so its
// loading flag was never cleared and "Loading Conversations" stayed on screen
// until the agent reloaded. Rack::Timeout cuts every request at 15s, so 30s only
// ever fires for a response that is never going to arrive.
//
// Replaces the axios interceptor production injected through vueapp.html.erb in
// wlab-report-fixes:20260914.1; behaviour is the same.
export const DEFAULT_REQUEST_TIMEOUT_MS = 30000;

const isFormDataUpload = data =>
  typeof FormData !== 'undefined' && data instanceof FormData;

// An upload has no fixed limit, but it must not wait forever either. A stalled
// network (nginx logs these as 408) left an attachment "sending" indefinitely,
// and because sends are serialised per conversation, every later reply in that
// chat queued behind it until the agent reloaded. Abort once nothing has moved
// for this long; it covers both the upload and the wait for the response, which
// Rack::Timeout caps at 15s.
export const UPLOAD_STALL_TIMEOUT_MS = 30000;
const UPLOAD_STALL_CHECK_MS = 5000;

const watchUploadForStall = config => {
  if (config.signal) return;

  const controller = new AbortController();
  const onUploadProgress = config.onUploadProgress;
  let lastActivityAt = Date.now();

  config.signal = controller.signal;
  config.onUploadProgress = event => {
    lastActivityAt = Date.now();
    if (onUploadProgress) onUploadProgress(event);
  };

  const timer = setInterval(() => {
    if (Date.now() - lastActivityAt < UPLOAD_STALL_TIMEOUT_MS) return;
    clearInterval(timer);
    controller.abort();
  }, UPLOAD_STALL_CHECK_MS);
  config.wlabStopStallWatch = () => clearInterval(timer);
};

const stopStallWatch = config => config?.wlabStopStallWatch?.();

// axios reports both a timeout and a browser-aborted request as ECONNABORTED;
// only the timeout carries "timeout" in its message. An aborted request was
// cancelled on purpose and must not come back.
const isTimeoutError = error =>
  error?.code === 'ETIMEDOUT' ||
  (error?.code === 'ECONNABORTED' && /timeout/i.test(String(error?.message)));

// Only a GET is replayed, and only once. Writes are never retried: the server
// may already have applied the first attempt, and replaying a POST has produced
// duplicate messages before.
const shouldReplayTimedOutRequest = error => {
  const config = error?.config;
  if (!config || !isTimeoutError(error)) return false;
  if (String(config.method || 'get').toLowerCase() !== 'get') return false;

  return !config.wlabTimeoutReplayed;
};

export default axios => {
  const { apiHost = '' } = window.chatwootConfig || {};
  const wootApi = axios.create({ baseURL: `${apiHost}/` });
  wootApi.defaults.timeout = DEFAULT_REQUEST_TIMEOUT_MS;

  // Uploads can legitimately run longer than any fixed limit.
  wootApi.interceptors.request.use(config => {
    if (isFormDataUpload(config.data)) {
      config.timeout = 0;
      watchUploadForStall(config);
    }
    return config;
  });

  // Every response carries the auth headers, rotated or not
  // (AUTH_TOKEN_ROTATE_PER_REQUEST). Keep what this tab sends up to date, but write
  // the shared cookie only when they changed and the cookie is not another
  // account's: rewriting it on every response made it flip between the accounts of
  // different tabs, and brought a signed-out session back.
  const syncRotatedAuthHeaders = response => {
    const authHeaders = extractAuthHeaders(response?.headers);
    if (!hasRequiredAuthHeaders(authHeaders) || !headersChanged(authHeaders)) {
      return response;
    }
    if (mayWriteAuthCookie(authHeaders)) storeAuthHeaders(authHeaders);
    Object.assign(wootApi.defaults.headers.common, authHeaders);
    setHeldAuth(authHeaders);
    return response;
  };

  // Add Auth Headers to requests if logged in
  if (Auth.hasAuthCookie()) {
    const {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    } = Auth.getAuthData();
    Object.assign(wootApi.defaults.headers.common, {
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    });
    setHeldAuth({
      'access-token': accessToken,
      'token-type': tokenType,
      client,
      expiry,
      uid,
    });
  }
  // Response parsing interceptor
  wootApi.interceptors.response.use(
    response => {
      stopStallWatch(response?.config);
      noteServerBuild(response?.headers);
      return syncRotatedAuthHeaders(response);
    },
    error => {
      stopStallWatch(error?.config);
      noteServerBuild(error?.response?.headers);
      if (shouldReplayTimedOutRequest(error)) {
        error.config.wlabTimeoutReplayed = true;
        return wootApi.request(error.config);
      }
      return parseErrorCode(error);
    }
  );
  return wootApi;
};
